import { Component, AfterViewInit, OnDestroy, ChangeDetectionStrategy, inject } from "@angular/core";

import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { WorkOSService } from "app/core/auth/workos.service";
import { environment } from "environments/environment";

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  features: string[];
  icon: string;
  svgIcon?: string;
  illustration: string;
  accentColor: string;
  accentLight: string;
  ready: boolean;
  url?: string;
}

@Component({
  selector: "app-landing",
  imports: [MatButtonModule, MatIconModule],
  templateUrl: "./landing.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./landing.component.scss",
})
export class LandingComponent implements AfterViewInit, OnDestroy {
  private workosService = inject(WorkOSService);

  mobileMenuOpen = false;
  productsDropdownOpen = false;
  private chevronObserver: IntersectionObserver | null = null;

  products: Product[] = [
    {
      id: "hyperlinking",
      name: "LinkWeaver",
      tagline: "Intelligent PDF Hyperlinking",
      description:
        "Automatically generate and manage hyperlinks, bookmarks, and cross-references in your PDF documents with AI-powered precision.",
      features: [
        "AI-powered link detection",
        "Cross-reference extraction",
        "Bookmark generation",
        "Batch processing",
      ],
      icon: "link",
      illustration: "images/products/illustration-hyperlinking.svg",
      accentColor: "#0b529a",
      accentLight: "#dbeafe",
      ready: true,
      url: environment.productUrls.linkWeaver,
    },
    {
      id: "translation",
      name: "PolyScribe",
      tagline: "AI Translation & Quality Analysis",
      description:
        "Translate regulatory documents across languages and verify accuracy with AI-powered back-translation quality analysis, similarity scoring, and meaning-change detection.",
      features: [
        "Back-translation verification",
        "Quality reports & similarity scoring",
        "Technical terminology preservation",
        "12+ languages supported",
      ],
      icon: "translate",
      svgIcon: "images/products/icon-polyscribe.svg",
      illustration: "images/products/illustration-translation.svg",
      accentColor: "#0891b2",
      accentLight: "#cffafe",
      ready: true,
      url: environment.productUrls.polyScribe,
    },
    {
      id: "formatting",
      name: "DocForge",
      tagline: "Professional Document Formatting",
      description:
        "Convert Word documents to polished PDFs with precise control over margins, fonts, headers, footers, and automatic table of contents generation.",
      features: [
        "Word to PDF conversion",
        "Custom margins & fonts",
        "Header & footer control",
        "Auto TOC generation",
      ],
      icon: "format_paint",
      illustration: "images/products/illustration-formatting.svg",
      accentColor: "#d97706",
      accentLight: "#fef3c7",
      ready: true,
      // DocForge is absorbed into LinkWeaver (T-13). Both tiles route to the
      // shared post-login /dashboard, which then offers the vertical chooser.
      url: environment.productUrls.linkWeaver,
    },
    {
      id: "comparison",
      name: "DeltaLens",
      tagline: "Smart Document Comparison",
      description:
        "Compare two documents side-by-side to highlight differences, track changes, and receive AI-powered suggestions for corrections.",
      features: [
        "Side-by-side diff view",
        "Change highlighting",
        "AI correction suggestions",
        "Version tracking",
      ],
      icon: "compare",
      illustration: "images/products/illustration-comparison.svg",
      accentColor: "#7c3aed",
      accentLight: "#ede9fe",
      ready: false,
    },
  ];

  ngAfterViewInit() {
    this.setupChevronFade();
  }

  ngOnDestroy() {
    this.chevronObserver?.disconnect();
  }

  private setupChevronFade() {
    const chevrons = document.querySelectorAll(".section-nav-chevron");
    if (!chevrons.length) return;

    this.chevronObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          el.style.opacity = entry.isIntersecting ? "1" : "0";
        });
      },
      { threshold: 0.5 },
    );

    chevrons.forEach((chevron) => this.chevronObserver!.observe(chevron));
  }

  navigateToLogin() {
    this.workosService.login();
  }

  onProductClick(product: Product) {
    if (product.ready && product.url) {
      window.location.href = product.url;
    } else if (product.ready) {
      this.workosService.login();
    }
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  toggleProductsDropdown() {
    this.productsDropdownOpen = !this.productsDropdownOpen;
  }

  closeProductsDropdown() {
    this.productsDropdownOpen = false;
  }

  scrollToProducts() {
    document
      .getElementById("products-section")
      ?.scrollIntoView({ behavior: "smooth" });
    this.closeProductsDropdown();
  }

  scrollToProduct(productId: string) {
    document
      .getElementById("product-" + productId)
      ?.scrollIntoView({ behavior: "smooth" });
    this.closeProductsDropdown();
    this.mobileMenuOpen = false;
  }

  scrollToNextProduct(currentIndex: number) {
    const nextProduct = this.products[currentIndex + 1];
    if (nextProduct) {
      document
        .getElementById("product-" + nextProduct.id)
        ?.scrollIntoView({ behavior: "smooth" });
    }
  }
}
